import { withAuth } from '../../context/Auth';
import withContainer from '../../components/Container/withContainer';
import {
    createImportDraft,
    getStudents,
    importStudents
} from '../../api/students/students';
import {
    StudentInfo,
    StudentsImportDraftResult,
    StudentFilters
} from '../../api/students/students.types';
import { ColumnProps, TableFilters } from '../../ui/Table';
import { PowerTable } from 'ui/PowerTable';
import { RouterPaths } from 'helpers/router-paths';
import { FC, useCallback, useMemo, useState } from 'react';
import { PopupChildrenRenderProps, usePopup } from 'components/Popup';
import { Button } from 'ui/Button';
import { Headline, TextM, TextXS } from 'ui/Styled/Styled';
import { InfoCard } from 'ui/Card';
import { cdnFileUrl } from 'utils/cdn';
import { FileCloud } from 'ui/FileCloud';
import { FileData } from 'api/file/file.types';
import styled, { css } from 'styled-components';
import { themeable, ThemeValueKey } from 'themes/utils';
import ButtonsContainer from 'ui/Button/ButtonContainer';
import { useQuery } from '@tanstack/react-query';
import { QueryKey } from 'helpers/constants';
import { toast } from 'react-toastify';
import { Skeleton } from 'components/Skeleton';
import { SkeletonTopCell } from 'components/Skeleton/Skeleton.styled';
import importIcon from 'assets/import-excel.png';

const STUDENTS_COLUMNS: ColumnProps<StudentInfo> = {
    avatar: { title: '', width: 60 },
    fullName: 'ФИО',
    phone: 'Номер телефона',
    birthday: 'Дата рождения',
    email: 'Почта'
};

const templateUrl = cdnFileUrl('template/import-students.xlsx');

const Container = styled.div`
    display: flex;
    flex-flow: column;
    gap: 20px;
`;

const Info = styled.div`
    display: flex;
    flex-flow: column;
    gap: 4px;
`;

const InfoRow = styled(TextM)`
    display: flex;
    flex-flow: row;
    align-items: center;

    gap: 4px;
`;

const ErrorsZone = styled(TextXS)`
    background: ${themeable('input.background')};
    padding: 14px;
    opacity: 0.8;
    display: flex;
    flex-flow: column;
    gap: 4px;
    border-radius: 8px;
    margin-top: 10px;
    max-height: 300px;
    overflow-y: auto;
`;

const ErrorRow = styled.div`
    display: flex;
    flex-flow: row;
    gap: 4px;
`;

const InfoValue = styled.div<{ $color?: ThemeValueKey }>`
    ${({ $color }) => css`
        color: ${themeable($color ?? 'primaryColor')};
    `};
`;

const ButtonsRow = styled(ButtonsContainer)`
    margin: 4px 0 0;
`;

const ImportStudentsPopupContent: FC<PopupChildrenRenderProps> = ({
    close
}) => {
    const [fileData, setFileData] = useState<FileData>();
    const [importDraft, setImportDraft] = useState<StudentsImportDraftResult>();
    const { refetch } = useQuery({ queryKey: [QueryKey.Student] });
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);

    const onFileUpload = useCallback((data: FileData) => {
        setLoading(true);
        setFileData(data);

        createImportDraft(data)((res) => {
            setImportDraft(res.data);
            setLoading(false);
        });
    }, []);

    const resetFile = useCallback(() => {
        setFileData(undefined);
        setImportDraft(undefined);
    }, []);

    const onSubmit = useCallback(() => {
        if (!importDraft?.draftId) {
            throw new Error('Empty draftId in importDraft');
        }
        setSubmitting(true);
        importStudents(importDraft.draftId)(
            async () => {
                await refetch();
                setSubmitting(false);
                setImportDraft(undefined);
                setFileData(undefined);
                close();
                toast.success(<>Персонал импортирован!</>);
            },
            () => {
                setSubmitting(false);
            }
        );
    }, [close, importDraft?.draftId, refetch]);

    return (
        <Container>
            <Headline>Импорт из Excel</Headline>
            <InfoCard
                title="Импорт по шаблону"
                description="Для импорта студентов необходимо скачать и заполнить шаблон."
                rightContent={
                    <Button
                        size="s"
                        href={templateUrl}
                        target="_blank"
                        download
                    >
                        Скачать шаблон
                    </Button>
                }
            />
            {loading && (
                <>
                    <Skeleton rows={3} header={false} />
                    <SkeletonTopCell />
                </>
            )}
            {!fileData && <FileCloud accept="table" onUpload={onFileUpload} />}
            {importDraft && (
                <>
                    <Info>
                        <InfoRow>
                            <TextM>Файл загружен:</TextM>
                            <InfoValue>{fileData?.fullName}</InfoValue>
                        </InfoRow>
                        <InfoRow>
                            <TextM>Статус:</TextM>
                            <InfoValue
                                $color={
                                    importDraft.isSuccess ? 'success' : 'error'
                                }
                            >
                                {importDraft.isSuccess
                                    ? 'Успешно'
                                    : 'Неуспешно'}
                            </InfoValue>
                        </InfoRow>
                        {importDraft.failResults?.length && (
                            <>
                                <InfoRow>
                                    <TextM>Строки с ошибками:</TextM>
                                    <InfoValue>
                                        {importDraft.failResults.length}
                                    </InfoValue>
                                </InfoRow>
                                <ErrorsZone>
                                    {importDraft.failResults.map(
                                        ({ rowNumber, errors }) => (
                                            <ErrorRow key={rowNumber}>
                                                <div>Строка {rowNumber}:</div>
                                                <div>
                                                    {errors?.map(
                                                        ({
                                                            propertyName,
                                                            code,
                                                            message
                                                        }) => (
                                                            <div
                                                                key={`${propertyName}-${code}`}
                                                            >
                                                                {message}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </ErrorRow>
                                        )
                                    )}
                                </ErrorsZone>
                            </>
                        )}
                    </Info>
                    <ButtonsRow>
                        {importDraft.isSuccess && (
                            <Button onClick={onSubmit} disabled={submitting}>
                                Импортировать
                            </Button>
                        )}
                        <Button onClick={resetFile}>Выбрать другой файл</Button>
                    </ButtonsRow>
                </>
            )}
        </Container>
    );
};

const filters: TableFilters<StudentInfo, StudentFilters> = {
    birthday: {
        component: 'date',
        column: 'birthday'
    }
};

const StudentsList = () => {
    const popupContent = useCallback(
        (props: PopupChildrenRenderProps) => (
            <ImportStudentsPopupContent {...props} />
        ),
        []
    );
    const openImportPopup = usePopup(popupContent);

    const headingRightContent = useMemo(
        () => (
            <Button
                view="success"
                onClick={openImportPopup}
                iconUrl={importIcon}
            >
                Импорт
            </Button>
        ),
        [openImportPopup]
    );

    return (
        <PowerTable
            queryKey={[QueryKey.Student]}
            columns={STUDENTS_COLUMNS}
            getter={getStudents}
            linkGetter={RouterPaths.StudentProfile}
            noRowsMsg="Персонал не найден"
            heading="Персонал"
            headingRightContent={headingRightContent}
            filters={filters}
        />
    );
};

export default withAuth(
    withContainer(StudentsList, {
        hideHeading: true,
        flex: true
    })
);
